import { defineStore } from "pinia";
import { ref } from "vue";
import Vue from "vue";
import { useLegalDataApi } from "@/composables/useLegalDataApi";
import { useCausaCaratulaStore } from "./useCausaCaratulaStore";
import { useJuzgadosStore } from "./useJuzgadosStore";
import { useModusOperandiStore } from "./useModusOperandiStore";

export const useLegalDataStore = defineStore("legalData", () => {
  const isView = ref(true);
  const isEdit = ref(false);
  const isLoading = ref(false);
  const isFormValid = ref(true);
  const datosLegales = ref(null);
  const error = ref(null);

  const modusStore = useModusOperandiStore();

  const datosComplementarios = ref({
    DenunciaID: "",
    DependenciaID: "",
    Fechainicio: "",
    TiposumarioID: "",
    RoljuzgadoactuacionID: "",
    ActuacionID: "",
    ActuacionufiID: "",
  });

  const formData = ref({
    legajoNumero: "",
    anio: "",
    juzgadoId: "",
    ufiId: "",
    fiscalId: "",
    ayudanteId: "",
    modusOperandiId: "",
    sitioId: "",
    articulos: [],
    causasCaratulas: [],
  });

  const modifiedFields = ref(new Set());

  const legalDataApi = useLegalDataApi();
  const causaCaratulaStore = useCausaCaratulaStore();
  const juzgadosStore = useJuzgadosStore();

  const startView = () => {
    isView.value = true;
    isEdit.value = false;
  };

  const startEdit = () => {
    isView.value = false;
    isEdit.value = true;
  };

  const setLoading = (value) => {
    isLoading.value = value;
  };

  const setFormValid = (value) => {
    isFormValid.value = value;
  };

  const reset = () => {
    isView.value = true;
    isEdit.value = false;
    isLoading.value = false;
    isFormValid.value = true;
    datosLegales.value = null;
    error.value = null;
    modifiedFields.value.clear();

    formData.value = {
      legajoNumero: "",
      anio: "",
      juzgadoId: "",
      ufiId: "",
      fiscalId: "",
      ayudanteId: "",
      modusOperandiId: "",
      sitioId: "",
      articulos: [],
      causasCaratulas: [],
    };
  };

  const markFieldAsModified = (fieldName) => {
    modifiedFields.value.add(fieldName);
  };
  const clearModifiedFields = () => {
    modifiedFields.value.clear();
  };

  const cancelEdit = async (actuacionId) => {
    startView();
    clearModifiedFields();
    if (actuacionId) {
      await fetchDatosLegales(actuacionId);
    }
  };

  // Función para inicializar formData desde datosLegales
  const initializeFormData = (typeActuation) => {
    // Si no hay datosLegales, inicializar con valores vacíos
    if (!datosLegales.value) {
      formData.value = {
        legajoNumero: "",
        anio: "",
        juzgadoId: "",
        ufiId: "",
        fiscalId: "",
        ayudanteId: "",
        modusOperandiId: "",
        sitioId: "",
        articulos: [],
        causasCaratulas: [],
      };
      clearModifiedFields();
      return;
    }

    const data = getLegalData(typeActuation);

    formData.value = {
      legajoNumero: data.legajoNumero,
      anio: data.anio,
      juzgadoId: data.juzgadoInterviniente.id,
      ufiId: data.ufi?.id || "",
      fiscalId: data.fiscalACargo?.id || "",
      ayudanteId: data.ayudanteFiscal?.id || "",
      modusOperandiId: data.modusOperandi.id || "",
      sitioId: data.sitio.id || "",
      articulos: data.articulos?.map((art) => art.id) || [],
      causasCaratulas: data.causasCaratulas?.map((causa) => causa.id) || [],
    };

    clearModifiedFields();
  };

  const fetchDatosLegales = async (actuacionId) => {
    isLoading.value = true;
    error.value = null;

    try {
      // VALIDAR que el ID sea un string/number válido
      if (!actuacionId || actuacionId === "null" || actuacionId === "undefined") {
        throw new Error("ID de actuación inválido: " + actuacionId);
      }

      // Convertir a string por si acaso
      const idString = String(actuacionId);

      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/actuacion/${idString}`;

      const response = await Vue.prototype.$rest.get({ url });
      
      // VERIFICAR la estructura de la respuesta
      if (response && response.data) {
        // Dependiendo de tu API, podría ser:
        // 1. response.data.data (array)
        // 2. response.data (objeto directo)
        // 3. response (objeto con data)

        if (response.data.data && Array.isArray(response.data.data)) {
          // Caso: response.data.data es un array
          if (response.data.data.length > 0) {
            datosLegales.value = response.data.data[0];
          } else {
            throw new Error("No se encontraron datos para la actuación");
          }
        } else if (response.data && !Array.isArray(response.data)) {
          // Caso: response.data es un objeto directo
          datosLegales.value = response.data;
        } else {
          // Caso: la respuesta es el array directamente
          datosLegales.value = response.data;
        }
      } else {
        throw new Error("Respuesta de API inválida");
      }

      // Solo continuar si datosLegales.value no es null
      if (!datosLegales.value) {
        throw new Error("No se pudieron cargar los datos legales");
      }

      // Guardar datos complementarios necesarios para los PUT
      datosComplementarios.value = {
        DenunciaID: datosLegales.value.DenunciaID || "",
        DependenciaID: datosLegales.value.DependenciaID || "",
        Fechainicio: datosLegales.value.FechaCreacion || "",
        TiposumarioID: datosLegales.value.TiposumarioID || "",
        RoljuzgadoactuacionID: datosLegales.value.Roljuzgadoactuacion?.RoljuzgadoactuacionID || "",
        ActuacionID: datosLegales.value.ActuacionID || "",
        ActuacionufiID: datosLegales.value.Actuacionufi?.[0]?.ActuacionufiID || "",
      };

      return datosLegales.value;
    } catch (e) {
      console.error("Error fetching datos legales:", e);
      error.value = e;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  const getLegalData = (typeActuation) => {
    const numero = datosLegales.value?.Numero || "";
    const match = numero.match(/\/(\d{2})$/);
    const isExpediente = typeActuation?.toLowerCase().includes("expediente");

    const legajoSinAnio = match ? numero.replace(/\/\d{2}$/, "") : numero;

    const baseData = {
      legajoNumero: legajoSinAnio,
      anio: match ? match[1] : "",
      sitio: {
        id: datosLegales.value?.TipoSitioID || "",
        nombre: datosLegales.value?.Sitio || "Sin registro",
      },
      modusOperandi: {
        // El catálogo usa TipomodusoperandiID, pero la API puede traer ModusoperandiID
        id: datosLegales.value?.Modusoperandi?.ModusoperandiID,
        nombre: datosLegales.value?.Modusoperandi?.Nombre || "",
      },
      interviniente: datosLegales.value?.Interviniente || "",
      juzgadoInterviniente: {
        id: datosLegales.value?.Roljuzgadoactuacion?.JuzgadoID || "",
        nombre: datosLegales.value?.Roljuzgadoactuacion?.Nombre || "",
      },
      causaCaratula: datosLegales.value?.CausaCaratula || "",
    };

    // Causas/Carátulas o Artículos según el tipo de actuación
    if (isExpediente) {
      // Para expedientes: usar artículos
      baseData.articulos = (datosLegales.value?.ActuacionArticulo || []).map((art) => ({
        id: art.ArticulosID,
        numeroArticulo: art.Numeroarticulo,
      }));
    } else {
      // Para otros tipos: usar causas/carátulas
      baseData.causasCaratulas = (datosLegales.value?.Actuacioncausacaratula || []).map(
        (causa) => ({
          id: causa.TipocausacaratulaID,
          nombre: causa.Nombre,
        })
      );
    }

    // Solo agregar datos de UFI si es "Unidades Fiscales"
    if (typeActuation?.includes("Unidades Fiscales")) {
      baseData.ufi = {
        id: datosLegales.value?.Actuacionufi?.[0]?.TipoufiID || "",
        nombre: datosLegales.value?.Actuacionufi?.[0]?.Numero || "",
      };
      baseData.fiscalACargo = {
        id: datosLegales.value?.Actuacionpersonalfiscal?.[0]?.PersonalfiscalID || "",
        nombre: datosLegales.value?.Actuacionpersonalfiscal?.[0]?.Denominacion || "",
      };
      baseData.ayudanteFiscal = {
        id: datosLegales.value?.Actuacionpersonalfiscal?.[1]?.PersonalfiscalID || "",
        nombre: datosLegales.value?.Actuacionpersonalfiscal?.[1]?.Denominacion || "",
      };
    }

    return baseData;
  };

  const buildLegajoNumero = (numero, anio) => {
    if (!numero) return "";
    if (!anio) return numero;

    const numeroLimpio = numero.replace(/\/\d{2}$/, "");

    return `${numeroLimpio}/${anio}`;
  };

  const getModifiedFields = () => {
    return modifiedFields.value;
  };
  const updateNumeroLegajo = async (actuacionId) => {
    try {
      const legajoNumeroCompleto = buildLegajoNumero(
        formData.value.legajoNumero,
        formData.value.anio
      );

      await legalDataApi.updateNumero(
        actuacionId,
        legajoNumeroCompleto,
        datosComplementarios.value
      );

      return true;
    } catch (e) {
      console.error("Error actualizando número de legajo:", e);
      throw e;
    }
  };

  const updateCausasCaratulas = async (actuacionId) => {
    try {
      const listaCaratulas = formData.value.causasCaratulas.map((causaId) => {
        const causa = causaCaratulaStore.causasCaratulas.find(
          (c) => c.TipocausacaratulaID === causaId
        );
        return {
          Descripcion: causa?.Descripcion || null,
          Nombre: causa?.Nombre || "",
          TipoCaratula: causa?.TipoCaratula || "",
          TipocausacaratulaID: causaId,
        };
      });

      await legalDataApi.updateCausasCaratulas(
        actuacionId,
        listaCaratulas,
        datosComplementarios.value
      );

      return true;
    } catch (e) {
      console.error("Error actualizando causas/carátulas:", e);
      throw e;
    }
  };

  const updateModusOperandiData = async (actuacionId) => {
    try {
      const modus = modusStore.modusOperandi.find(
        (m) => m.TipomodusoperandiID === formData.value.modusOperandiId
      );

      const modusOperandi = {
        Nombre: modus?.Nombre || "",
        Descripcion: modus?.Descripcion || null,
      };

      await legalDataApi.updateModusOperandi(actuacionId, modusOperandi);

      return true;
    } catch (e) {
      console.error("Error actualizando modus operandi:", e);
      throw e;
    }
  };

  const updateSitioData = async (actuacionId) => {
    try {
      await legalDataApi.updateSitio(actuacionId, formData.value.sitioId);

      return true;
    } catch (e) {
      console.error("Error actualizando sitio:", e);
      throw e;
    }
  };

  const updateJuzgadoData = async () => {
    try {
      const RoljuzgadoactuacionID = datosComplementarios.value?.RoljuzgadoactuacionID;
      const juzgadoId = formData.value?.juzgadoId;

      const esDependencia =
        juzgadoId === "DEPENDENCIA" ||
        juzgadoId === null ||
        juzgadoId === undefined ||
        juzgadoId === "";

      if (RoljuzgadoactuacionID && esDependencia) {
        const backendData = {};

        try {
          const respuesta = await legalDataApi.updateJuzgado(RoljuzgadoactuacionID, backendData);

          if (respuesta && respuesta.error) {
            console.error("Error del backend:", respuesta.error);
          }
        } catch (error) {
          console.error("Error en la llamada a la API:", error);
        }
        return true;
      }

      if (!RoljuzgadoactuacionID && esDependencia) {
        return true;
      }

      const juzgadoActual = datosLegales.value?.Roljuzgadoactuacion;
      const juzgadoFromCatalog = juzgadosStore.allJuzgados.find(
        (item) => String(item.JuzgadoID) === String(juzgadoId)
      );
      const juzgado =
        juzgadoFromCatalog ||
        (String(juzgadoActual?.JuzgadoID) === String(juzgadoId)
          ? {
              JuzgadoID: juzgadoActual.JuzgadoID,
              Nombre: juzgadoActual.Nombre,
              Descripcion: juzgadoActual.Descripcion,
            }
          : null);

      const fechaActual = new Date();
      const fechaFormateada = `${fechaActual.getFullYear()}-${String(fechaActual.getMonth() + 1).padStart(2, "0")}-${String(fechaActual.getDate()).padStart(2, "0")} ${String(fechaActual.getHours()).padStart(2, "0")}:${String(fechaActual.getMinutes()).padStart(2, "0")}:${String(fechaActual.getSeconds()).padStart(2, "0")}`;

      const juzgadoData = {
        ActuacionID: datosComplementarios.value.ActuacionID,
        Descripcion: `Modificado el dia ${fechaFormateada}`,
        Nombre: juzgado?.Nombre || "",
        JuzgadoID: juzgadoId,
      };

      await legalDataApi.updateJuzgado(RoljuzgadoactuacionID, juzgadoData);

      return true;
    } catch (e) {
      console.error("Error actualizando juzgado:", e);
      throw e;
    }
  };

  const updateArticulosData = async (actuacionId) => {
    try {
      const articulosExistentes = datosLegales.value?.ActuacionArticulo || [];

      await legalDataApi.updateArticulos(
        actuacionId,
        formData.value.articulos,
        articulosExistentes
      );

      return true;
    } catch (e) {
      console.error("Error actualizando artículos:", e);
      throw e;
    }
  };

  const updateUfiData = async (actuacionId) => {
    try {
      await legalDataApi.updateUfi(
        datosComplementarios.value.ActuacionufiID,
        formData.value.ufiId,
        actuacionId
      );

      return true;
    } catch (e) {
      console.error("Error actualizando UFI:", e);
      throw e;
    }
  };

  const updateFiscalData = async (actuacionId) => {
    try {
      const fiscalData = {
        PersonalfiscalID: formData.value.fiscalId,
      };

      await legalDataApi.updateFiscal(actuacionId, fiscalData);

      return true;
    } catch (e) {
      console.error("Error actualizando fiscal:", e);
      throw e;
    }
  };

  const updateAyudanteFiscalData = async (actuacionId) => {
    try {
      const ayudanteData = {
        PersonalfiscalID: formData.value.ayudanteId,
      };

      await legalDataApi.updateAyudanteFiscal(actuacionId, ayudanteData);

      return true;
    } catch (e) {
      console.error("Error actualizando ayudante fiscal:", e);
      throw e;
    }
  };

  const saveDatosLegales = async (actuacionId) => {
    isLoading.value = true;
    error.value = null;

    try {
      const fieldsToUpdate = getModifiedFields();

      const updateMap = {
        legajoNumero: () => updateNumeroLegajo(actuacionId),
        anio: () => updateNumeroLegajo(actuacionId),
        causasCaratulas: () => updateCausasCaratulas(actuacionId),
        modusOperandiId: () => updateModusOperandiData(actuacionId),
        sitioId: () => updateSitioData(actuacionId),
        juzgadoId: () => updateJuzgadoData(),
        articulos: () => updateArticulosData(actuacionId),
        ufiId: () => updateUfiData(actuacionId),
        fiscalId: () => updateFiscalData(actuacionId),
        ayudanteId: () => updateAyudanteFiscalData(actuacionId),
      };

      for (const [field, updateFn] of Object.entries(updateMap)) {
        if (fieldsToUpdate.has(field)) {
          await updateFn();
        }
      }

      clearModifiedFields();
      await fetchDatosLegales(actuacionId);

      return true;
    } catch (e) {
      console.error("Error guardando datos legales:", e);
      error.value = e;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State - UI
    isView,
    isEdit,
    isLoading,
    isFormValid,

    // State - Data
    datosLegales,
    error,
    formData,

    // Actions - UI
    startView,
    startEdit,
    setLoading,
    setFormValid,
    reset,
    cancelEdit,

    // Actions - Data
    fetchDatosLegales,
    saveDatosLegales,
    getLegalData,
    buildLegajoNumero,
    initializeFormData,

    // Actions - Tracking
    markFieldAsModified,
    clearModifiedFields,
    getModifiedFields,
  };
});
